/**
 * HopeModal - Popup modal for Hope AI companion
 *
 * Opens as an overlay when patient clicks the floating heart.
 * Doesn't navigate away from current page.
 *
 * NOW WITH FULL CONTEXT AWARENESS:
 * - Current time and timezone
 * - Current page location
 * - Patient's diagnoses, prescriptions, appointments
 * - Page-specific contextual greetings
 *
 * Layout:
 * - Left (50%): Chat interface with full context
 * - Right (50%): 3D Character with Voice
 * - Mobile: Stacks vertically
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import HopeContextChat from './HopeContextChat';
import Hope3DCharacterSDK from './Hope3DCharacterSDK';
import { patientContextService } from '../services/PatientContextService';
import { getPageContext } from '../services/HopeContextService';
import { convaiConfig } from '../config/convai';

// Pre-connect to Convai API for faster first response
if (typeof document !== 'undefined') {
  const link = document.createElement('link');
  link.rel = 'preconnect';
  link.href = 'https://api.convai.com';
  link.crossOrigin = 'anonymous';
  document.head.appendChild(link);
}

interface HopeModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientId: string;
  patientName?: string;
}

export default function HopeModal({ isOpen, onClose, patientId, patientName }: HopeModalProps) {
  const [patientContext, setPatientContext] = useState<string>('');
  const [isLoadingContext, setIsLoadingContext] = useState(true);
  const [suggestedTopics, setSuggestedTopics] = useState<string[]>([]);
  const [hasEverOpened, setHasEverOpened] = useState(false);
  const location = useLocation();
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const contextCacheRef = useRef<{ patientId: string; context: string; topics: string[]; timestamp: number } | null>(null);

  // Track first open — after first open, keep modal mounted (hidden) for instant re-open
  useEffect(() => {
    if (isOpen && !hasEverOpened) {
      setHasEverOpened(true);
    }
  }, [isOpen, hasEverOpened]);

  // Save focus on open, restore on close
  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement;
    } else if (previousFocusRef.current) {
      previousFocusRef.current.focus();
      previousFocusRef.current = null;
    }
  }, [isOpen]);

  // Escape key + focus trap
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      // Focus trap: Tab cycles within modal
      if (e.key === 'Tab' && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };
    document.addEventListener('keydown', handler);

    // Auto-focus close button on open
    const timer = setTimeout(() => {
      const closeBtn = modalRef.current?.querySelector<HTMLElement>('[aria-label="Close Hope AI Assistant"]');
      closeBtn?.focus();
    }, 100);

    return () => {
      document.removeEventListener('keydown', handler);
      clearTimeout(timer);
    };
  }, [isOpen, onClose]);

  // Build complete context whenever modal opens or page changes
  useEffect(() => {
    if (isOpen && patientId) {
      buildPatientContext();
    }
  }, [isOpen, patientId, location.pathname]);

  const buildPatientContext = async () => {
    try {
      // Use cached context if available and fresh (< 5 minutes)
      const cache = contextCacheRef.current;
      if (cache && cache.patientId === patientId && (Date.now() - cache.timestamp) < 5 * 60 * 1000) {
        setPatientContext(cache.context);
        setSuggestedTopics(cache.topics);
        setIsLoadingContext(false);
        return;
      }

      setIsLoadingContext(true);

      // Get patient medical context (diagnoses, meds, appointments)
      const context = await patientContextService.getPatientContext(patientId);

      // Get current page context
      const pageContext = getPageContext(location.pathname);
      setSuggestedTopics(pageContext.suggestedTopics || []);

      if (context) {
        // Format complete context for Convai with page awareness
        const contextString = `
${context.contextSummary}

Current page: You are on the "${pageContext.pageName}" page of the app.
${pageContext.contextualGreeting}
        `.trim();

        console.log('🔍 HopeModal - Built patient context:', contextString);
        setPatientContext(contextString);
      } else {
        // Fallback context if patient data not available
        const now = new Date();
        const currentTime = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
        const currentDate = now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const fallbackContext = `
Current system time: ${currentTime} on ${currentDate}. Patient's timezone: ${timezone}.

You are talking to ${patientName || 'a patient'}.

Current page: You are on the "${pageContext.pageName}" page of the app.
${pageContext.contextualGreeting}
        `.trim();

        console.log('🔍 HopeModal - Using fallback context (no patient data):', fallbackContext);
        setPatientContext(fallbackContext);
      }
    } catch (error) {
      console.error('Error building patient context:', error);
      // Set minimal context on error
      setPatientContext(`You are talking to ${patientName || 'a patient'}.`);
    } finally {
      setIsLoadingContext(false);
    }
  };

  // Cache context after it's loaded
  useEffect(() => {
    if (patientContext && patientId && !isLoadingContext) {
      contextCacheRef.current = {
        patientId,
        context: patientContext,
        topics: suggestedTopics,
        timestamp: Date.now(),
      };
    }
  }, [patientContext, patientId, isLoadingContext, suggestedTopics]);

  // Don't render anything until first open (avoids loading 3D character on page load)
  // After first open, keep mounted but hidden for instant re-open
  if (!isOpen && !hasEverOpened) return null;

  return (
    <div style={{ display: isOpen ? 'block' : 'none' }}>
      {/* Animations & Mobile responsive */}
      <style>{`
        @keyframes hopeSlideUp {
          from { opacity: 0; transform: translate(-50%, -45%); }
          to { opacity: 1; transform: translate(-50%, -50%); }
        }
        @keyframes hopeFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @media (max-width: 768px) {
          .hope-chat-modal {
            width: 100% !important;
            height: 100% !important;
            max-height: 100% !important;
            border-radius: 0 !important;
          }
          .hope-modal-content {
            flex-direction: column !important;
          }
          .hope-left-panel {
            width: 100% !important;
            border-right: none !important;
            border-bottom: 1px solid #e0e0e0 !important;
            max-height: 50vh !important;
          }
          .hope-right-panel {
            width: 100% !important;
          }
        }
      `}</style>

      {/* Backdrop */}
      <div role="presentation" style={styles.backdrop} onClick={onClose}></div>

      {/* Modal — split layout: Chat left, 3D Character right */}
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="hope-modal-title"
        aria-describedby="hope-modal-description"
        style={styles.modal}
        className="hope-chat-modal"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          aria-label="Close Hope AI Assistant"
          style={styles.closeButton}
        >
          <span aria-hidden="true">×</span>
        </button>

        {/* Split Layout */}
        <div style={styles.splitContainer} className="hope-modal-content">
          {/* Left: Chat */}
          <div style={styles.leftPanel} className="hope-left-panel">
            {/* Header */}
            <div style={styles.header}>
              <div style={styles.headerContent}>
                <span style={styles.heartIcon} aria-hidden="true">💙</span>
                <div>
                  <h3 id="hope-modal-title" style={styles.headerTitle}>Ask Hope</h3>
                  <p id="hope-modal-description" style={styles.headerSubtitle}>Your mental health support between sessions</p>
                </div>
              </div>
            </div>

            {/* Chat Interface */}
            <div style={styles.chatPanel}>
              {isLoadingContext ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                  Loading context...
                </div>
              ) : (
                <HopeContextChat
                  patientContext={patientContext}
                  characterId={convaiConfig.characterId}
                  apiKey={convaiConfig.apiKey}
                  patientId={patientId}
                  patientName={patientName}
                  suggestedTopics={suggestedTopics}
                />
              )}
            </div>
          </div>

          {/* Right: 3D Character */}
          <div style={styles.rightPanel} className="hope-right-panel">
            <Hope3DCharacterSDK patientId={patientId} patientName={patientName} />
          </div>
        </div>
      </div>
    </div>
  );
}

// Styles
const styles: { [key: string]: React.CSSProperties } = {
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    zIndex: 9998,
    animation: 'hopeFadeIn 0.2s ease',
  },
  modal: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: '1100px',
    height: '75vh',
    maxHeight: '600px',
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    zIndex: 9999,
    overflow: 'hidden',
    animation: 'hopeSlideUp 0.25s ease',
    display: 'flex',
    flexDirection: 'column',
  },
  closeButton: {
    position: 'absolute',
    top: '12px',
    right: '12px',
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    border: 'none',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    color: '#0A2540',
    fontSize: '22px',
    fontWeight: 'bold',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
    transition: 'all 0.2s ease',
  },
  splitContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    overflow: 'hidden',
  },
  leftPanel: {
    width: '50%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRight: '1px solid #e0e0e0',
    overflow: 'hidden',
  },
  header: {
    background: 'linear-gradient(135deg, #87CEEB 0%, #5DADE2 100%)',
    padding: '14px 20px',
    borderBottom: '2px solid #5DADE2',
    flexShrink: 0,
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  heartIcon: {
    fontSize: '28px',
    lineHeight: '1',
  },
  headerTitle: {
    margin: 0,
    color: '#0A2540',
    fontSize: '20px',
    fontWeight: 'bold',
    lineHeight: '1.2',
  },
  headerSubtitle: {
    margin: 0,
    color: '#0F3D5C',
    fontSize: '12px',
    lineHeight: '1.3',
  },
  chatPanel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  rightPanel: {
    width: '50%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
};
